import { ReactElement, useState } from 'react';
import { HiChevronRight, HiTrash } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import PencilIcon from '../../icons/PencilIcon';
import Button from '../../components/Button';
import { ChainModel } from 'types/ChainModel';
import {
  useChainModels,
  useCreateChainModel,
  useUpdateChainModel,
  useDeleteChainModel,
} from 'hooks/useChainModels';

interface ChainModelFormState {
  name: string;
  is_default: boolean;
  notes: string;
  modelJson: string;
}

const EMPTY_FORM: ChainModelFormState = {
  name: '',
  is_default: false,
  notes: '',
  modelJson: '',
};

function validateJson(text: string): boolean {
  if (!text.trim()) return true;
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

interface Props {
  teamId: string | null;
  teamName: string;
}

export default function ChainModelsSection({
  teamId,
  teamName,
}: Props): ReactElement {
  const {
    data: chainModels,
    isLoading,
  } = useChainModels();
  const createMutation = useCreateChainModel();
  const updateMutation = useUpdateChainModel();
  const deleteMutation = useDeleteChainModel();

  const [isExpanded, setIsExpanded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ChainModelFormState>(EMPTY_FORM);
  const [jsonError, setJsonError] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const teamModels = (chainModels ?? []).filter(
    (m: ChainModel) =>
      m.team_id === teamId || m.team_id === null
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setJsonError(false);
  };

  const startEdit = (model: ChainModel) => {
    setEditingId(model.id);
    setForm({
      name: model.name,
      is_default: model.is_default,
      notes: model.notes ?? '',
      modelJson: model.model
        ? JSON.stringify(model.model, null, 2)
        : '',
    });
    setJsonError(false);
    setIsExpanded(true);
  };

  const handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      try {
        const data = JSON.parse(text);
        const extracted: Partial<ChainModelFormState> = {};
        if (data.name) {
          extracted.name = String(data.name)
            .replace(/_/g, ' ')
            .replace(
              /\b\w+/g,
              (w) => w[0].toUpperCase() + w.slice(1).toLowerCase(),
            );
          delete data.name;
        }
        if (data.notes != null) {
          extracted.notes = String(data.notes);
          delete data.notes;
        }
        setForm((prev) => ({
          ...prev,
          ...extracted,
          modelJson: JSON.stringify(data, null, 2),
        }));
        setJsonError(false);
      } catch {
        toast.error('File does not contain valid JSON');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (form.modelJson.trim() && !validateJson(form.modelJson)) {
      toast.error('Invalid JSON');
      return;
    }

    const parsedModel = form.modelJson.trim()
      ? JSON.parse(form.modelJson)
      : null;

    const payload = {
      name: form.name.trim(),
      team_id: teamId,
      parent_id: null,
      is_default: form.is_default,
      model: parsedModel,
      notes: form.notes.trim() || null,
    };

    if (editingId) {
      updateMutation.mutate(
        { id: editingId, data: payload },
        {
          onSuccess: () => {
            toast.success('Chain model updated');
            resetForm();
          },
          onError: () => toast.error('Failed to update'),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Chain model created');
          resetForm();
        },
        onError: () => toast.error('Failed to create'),
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success('Chain model deleted');
        setDeletingId(null);
        if (editingId === id) resetForm();
      },
      onError: () => toast.error('Failed to delete'),
    });
  };

  const isSaving =
    createMutation.isPending || updateMutation.isPending;
  const isFormValid =
    form.name.trim().length > 0 && !jsonError;

  const inputCls = [
    'cr-mt-1 cr-w-full cr-rounded-md cr-border',
    'cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm',
    'focus:cr-border-blue-500 focus:cr-outline-none',
  ].join(' ');

  return (
    <div className="cr-mb-4 cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
      <button
        className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="cr-text-lg cr-font-semibold">
          Chain Models
        </h3>
        <HiChevronRight
          className={`cr-transition-transform cr-duration-200 ${isExpanded ? 'cr-rotate-90' : ''}`}
          size={20}
        />
      </button>

      {isExpanded && (
        <div className="cr-border-t cr-border-gray-200 cr-p-3">
          {/* Form */}
          <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-3">
            <h4 className="cr-text-sm cr-font-semibold cr-text-gray-700">
              {editingId ? 'Edit Chain Model' : 'Add Chain Model'}
              <span className="cr-ml-2 cr-text-xs cr-font-normal cr-text-gray-400">
                Team: {teamName}
              </span>
            </h4>

            <label className="cr-flex cr-flex-col">
              <span className="cr-text-xs cr-font-medium cr-text-gray-500">
                Name
              </span>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
                placeholder="e.g. SRAM_Measured_M41"
              />
            </label>

            <label className="cr-flex cr-items-center cr-gap-2">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_default: e.target.checked,
                  })
                }
                className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300"
              />
              <span className="cr-text-sm cr-text-gray-700">
                Default model for this team
              </span>
            </label>

            <label className="cr-flex cr-flex-col">
              <span className="cr-text-xs cr-font-medium cr-text-gray-500">
                Notes
              </span>
              <textarea
                className={`${inputCls} cr-resize-y`}
                value={form.notes}
                onChange={(e) =>
                  setForm({ ...form, notes: e.target.value })
                }
                placeholder="Optional description"
                rows={2}
                maxLength={1024}
              />
            </label>

            <label className="cr-flex cr-flex-col">
              <span className="cr-text-xs cr-font-medium cr-text-gray-500">
                Model File
              </span>
              <input
                type="file"
                accept=".json"
                onChange={handleFileLoad}
                className={[
                  inputCls,
                  'cr-cursor-pointer cr-file:mr-3',
                  'cr-file:rounded cr-file:border-0',
                  'cr-file:bg-blue-50',
                  'cr-file:px-3 cr-file:py-1',
                  'cr-file:text-sm',
                  'cr-file:font-medium',
                  'cr-file:text-blue-700',
                ].join(' ')}
              />
              {form.modelJson && (
                <span className="cr-mt-1 cr-text-xs cr-text-green-600">
                  Model loaded
                </span>
              )}
            </label>

            <div className="cr-flex cr-gap-2">
              <Button
                onClick={handleSave}
                disabled={!isFormValid || isSaving}
                className="secondary-button cr-w-fit"
              >
                {isSaving
                  ? 'Saving...'
                  : editingId
                    ? 'Update'
                    : 'Save'}
              </Button>
              {editingId && (
                <Button
                  onClick={resetForm}
                  className="cr-w-fit cr-rounded-md cr-bg-gray-200 cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-text-gray-700 hover:cr-bg-gray-300"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="cr-border-t cr-border-gray-100 cr-pt-3">
            <h4 className="cr-mb-2 cr-text-sm cr-font-semibold cr-text-gray-700">
              Existing Models
            </h4>
            {isLoading ? (
              <p className="cr-text-sm cr-text-gray-500">
                Loading...
              </p>
            ) : teamModels.length === 0 ? (
              <p className="cr-text-sm cr-text-gray-400">
                No chain models yet
              </p>
            ) : (
              <div className="cr-space-y-2">
                {teamModels.map((model: ChainModel) => (
                  <div
                    key={model.id}
                    className="cr-flex cr-items-start cr-justify-between cr-rounded-md cr-border cr-border-gray-200 cr-p-3 hover:cr-bg-gray-50"
                  >
                    <div className="cr-flex-1 cr-min-w-0">
                      <div className="cr-flex cr-items-center cr-gap-2">
                        <span className="cr-font-medium cr-truncate">
                          {model.name}
                        </span>
                        {model.is_default && (
                          <span className="cr-shrink-0 cr-rounded-full cr-bg-blue-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-blue-700">
                            Default
                          </span>
                        )}
                        {model.team_id === null && (
                          <span className="cr-shrink-0 cr-rounded-full cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium cr-text-gray-500">
                            Global
                          </span>
                        )}
                      </div>
                      {model.notes && (
                        <p className="cr-mt-1 cr-text-xs cr-text-gray-500 cr-truncate">
                          {model.notes}
                        </p>
                      )}
                    </div>

                    {model.team_id !== null && (
                    <div className="cr-ml-2 cr-flex cr-shrink-0 cr-gap-1">
                      <button
                        className="cr-flex cr-items-center cr-justify-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-p-2 cr-text-gray-600 hover:cr-bg-gray-100"
                        onClick={() => startEdit(model)}
                        aria-label={`Edit ${model.name}`}
                      >
                        <PencilIcon size={16} />
                      </button>

                      {deletingId === model.id ? (
                        <div className="cr-flex cr-gap-1">
                          <button
                            className="cr-rounded-md cr-bg-red-600 cr-px-2 cr-py-1 cr-text-xs cr-font-medium cr-text-white hover:cr-bg-red-700"
                            onClick={() =>
                              handleDelete(model.id)
                            }
                            disabled={
                              deleteMutation.isPending
                            }
                          >
                            Confirm
                          </button>
                          <button
                            className="cr-rounded-md cr-bg-gray-200 cr-px-2 cr-py-1 cr-text-xs cr-font-medium cr-text-gray-700 hover:cr-bg-gray-300"
                            onClick={() =>
                              setDeletingId(null)
                            }
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          className="cr-flex cr-items-center cr-justify-center cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-p-2 cr-text-gray-600 hover:cr-bg-red-50 hover:cr-text-red-600"
                          onClick={() =>
                            setDeletingId(model.id)
                          }
                          aria-label={`Delete ${model.name}`}
                        >
                          <HiTrash size={16} />
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
